import type { QueryClient } from '@tanstack/react-query'
import { timeKeys } from '../api/queries'
import type { Running, TimeEntry } from '../model/schema'

// Мой идущий интервал по конкретной задаче — по нему кнопка решает «веду я» и
// живёт живой тик. Чужой таймер сюда не попадает: список — только мой.
export function myRunningOn(list: Running[] | undefined, taskId: string): Running | undefined {
  return list?.find((r) => r.taskId === taskId)
}

// Самая свежая из идущих — её показывает строка таймер-бара (список приходит
// отсортированным, но порядок не гарантируем оптимистичными вставками).
export function latestRunning(list: Running[] | undefined): Running | undefined {
  if (!list || list.length === 0) return undefined
  return [...list].sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))[0]
}

// Оптимистичное добавление в список идущих: карточка и таймер-бар меняют вид
// сразу, а не после ответа. Иначе футер карточки перемигивает (Σ → ●идёт) и
// полоса таймера вздрагивает — состояние на миг остаётся прежним при уже
// переехавшей карточке. Повторный вход по той же задаче не дублирует строку.
export function addRunningToCache(qc: QueryClient, running: Running): void {
  qc.setQueryData<Running[]>(timeKeys.running(), (list) => [
    running,
    ...(list ?? []).filter((r) => r.taskId !== running.taskId),
  ])
}

// Оптимистичное гашение: задача отпущена — её строка уходит из бара сразу.
export function removeRunningFromCache(qc: QueryClient, taskId: string): void {
  qc.setQueryData<Running[]>(timeKeys.running(), (list) =>
    (list ?? []).filter((r) => r.taskId !== taskId),
  )
}

// Заготовка running для задачи, которую только что взяли в работу: entryId придёт
// с сервера, до тех пор он не нужен — вид зависит от startedAt/taskId.
export function optimisticRunning(task: {
  id: string
  title: string
  boardId: string
  columnId: string
}): Running {
  return {
    entryId: '',
    startedAt: new Date().toISOString(),
    taskId: task.id,
    taskTitle: task.title,
    boardId: task.boardId,
    columnId: task.columnId,
  }
}

// Длительность закрытого интервала в секундах (0 — если ещё идёт: там живой тик).
export function entryDurationSeconds(entry: TimeEntry): number {
  if (entry.stoppedAt === null) return 0
  return Math.max(0, Math.floor((Date.parse(entry.stoppedAt) - Date.parse(entry.startedAt)) / 1000))
}

export function addEntryToCache(qc: QueryClient, taskId: string, entry: TimeEntry): void {
  qc.setQueryData<TimeEntry[]>(timeKeys.entries(taskId), (list) => [...(list ?? []), entry])
}

export function updateEntryInCache(
  qc: QueryClient,
  taskId: string,
  entryId: string,
  patch: Partial<TimeEntry>,
): void {
  qc.setQueryData<TimeEntry[]>(timeKeys.entries(taskId), (list) =>
    list?.map((e) => (e.id === entryId ? { ...e, ...patch } : e)),
  )
}

export function removeEntryFromCache(qc: QueryClient, taskId: string, entryId: string): void {
  qc.setQueryData<TimeEntry[]>(timeKeys.entries(taskId), (list) =>
    list?.filter((e) => e.id !== entryId),
  )
}
